import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import UserPrivateRoute from './routes/userPrivateRoutes';
import CompanyPrivateRoute from './routes/companyPrivateRoute';
import AdminPrivateRoute from './routes/AdminPrivateRoute';
import PublicRoute from './routes/PublicRoutes';
import { isDesktopApp } from './utils/desktopBridge';
import SignupForm from './Company/SignUp/Signup';
import LoginPage from './Company/Login/LoginPage';
import AdminLogin from './Company/Login/AdminLoginPage';
// Shared LinkedIn OAuth callback — handles BOTH user and company sign-in flows
// (which one initiated is stored in sessionStorage by SocialLogin.jsx).
import LinkedInCallback from './Company/Login/LinkedInCallback';
import UserSignupForm from './User/SignUp/Signup';
import UserLoginPage from './User/Login/LoginPage';
import Home from './User/components/Home';
import Instructions from './User/components/Instructions';
import TestEntryGate from './User/components/TestEntryGate';
import Interview from './User/components/Interview';
import Questions from './User/components/Questions';
import Resultv2 from './User/Result/Resultv2';
import ContactPage from './ContactUs/Contact';
import CompanyHome from './components/CompanyHome';
import AllJob from './Company/CompanyHome/Home2V2';
import Ranking from './components/Ranking';
import Jd from './components/Jd';
import JobDescriptionPreview from './Company/JD/AfterJD';
import AfterLogin from "./pages/AfterLogin";
import Screening from './components/Screening';
import AfterGetStartedOnScreeningTest from "./pages/AfterGetStartedOnScreeningTest";
import PersonalityTest from "./pages/PersonalityTest";
import OverallTest from './pages/OverallTest';
import Thankyou from './User/components/Thankyou';
import UserDashboard from "./pages/UserDashboard";
import ScheduleTest from "./components/ScheduleTest";
import UploadJobDescription from './Company/JD/UploadJD';
import JdAi from './Company/JD/JdAi';
import JdAudioAI from './Company/JD/JdAudioAI';
// import All2 from './Company/Ranking/All2';
import InputDesign from './User/res/InputDesign';
import CreateResume from './User/res/createResume/CreateResume';
import MobileVideoUpload from './User/res/createVideo/MobileVideoUpload';
import AccountSecurity from './User/Account/AccountSecurity';
import MainResult from './User/IndividualResult/MainResult';
import Coding from './User/components/Coding';
import { RecruitmentHero } from './Desktop/RecruitmentHero';
import About from './Company/About/About';
import StartVideo from './User/res/createVideo/StartVideo';
// import Practice from './User/Coding/iPractice';
import PPractice from './User/Practice/Practice';
import ForgotPassword from './User/ForgotPass/ForgotPassword';
import ResetPassword from './User/ForgotPass/ResetPassword';
import EmpForgotPass from './Company/Forgetpassword/EmpForgotPass';
import EmpResetPass from './Company/Forgetpassword/EmpResetPass';
import Mock from './User/components/MockTest';
import CompanyAdmin from './CompanyAdmin/CompanyAdmin';
import Content from './CompanyAdmin/Content';
import TestResults from './User/Mock/InterviewResult';
import TestCards from './CompanyAdmin/Content/CompanyActions/JobActions/TestCards';
import ViewTest from './CompanyAdmin/Content/CompanyActions/JobActions/ViewTest';
import UserAdmin from './UserAdmin/UserAdmin'
import Reports from './User/IndividualResult/Reports';
import ResumeLandingPage from './resumetemplate/ResumeLandingPage';
import CvTemplatesPage from './resumetemplate/CvTemplatesPage';
import QuestionnairePage from './User/Questions/QuestionnairePage';
import RealtimeQuestionSection from './User/Questions/RealTimeInterview';
import QuickCodingTest from './User/Coding/QuickCodingTest';
import AptitudeTest from './User/Aptitude/AptitudeTest';
import OrganizerLogin from './Organizer/Auth/OrganizerLogin';
import OrganizerRegister from './Organizer/Auth/OrganizerRegister';
import VoiceAssistantOnboarding from './Organizer/Auth/VoiceAssistantOnboarding';
import OrganizerDashboard from './Organizer/Dashboard/OrganizerDashboard';
import OrganizerLanding from './Organizer/OrganizerLanding';
import EmailVerification from './Participant/EmailVerification';
import InviteLanding from './Participant/InviteLanding';
import CompletePage from './Participant/CompletePage';
import { OnboardingRealtime } from './Organizer/Auth/OrganizerOnboarding';
import InterviewerFeedbackPage from './Meeting/InterviewerFeedbackPage';

function safeStoredBool(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'false') === true;
  } catch {
    return false;
  }
}

function DesktopAuthGate() {
  const location = useLocation();
  const { isUserAuthenticated, isCompanyAuthenticated, isAdminAuthenticated } = useAuth();

  const isAuthed =
    !!isUserAuthenticated ||
    !!isCompanyAuthenticated ||
    !!isAdminAuthenticated ||
    safeStoredBool('isUserAuthenticated') ||
    safeStoredBool('isCompanyAuthenticated') ||
    safeStoredBool('isAdminAuthenticated');

  // In the desktop app we want unauthenticated users to land on the user login page.
  // Allow the auth/reset pages through to avoid redirect loops.
  const path = location.pathname;
  const isAllowedUnauthedPath =
    path === '/login_user' ||
    path === '/signin_user' ||
    path === '/forgot-password' ||
    path.startsWith('/reset-password/') ||
    path === '/interview-feedback' ||
    path === '/interview-room' ||
    path.startsWith('/interview-room/') ||
    path === '/interview-booking' ||
    path.startsWith('/interview-booking/');

  if (isDesktopApp() && !isAuthed && !isAllowedUnauthedPath) {
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login_user?next=${encodeURIComponent(next)}`} replace />;
  }

  return null;
}

function resolveLiveInterviewFrontendOrigin() {
  const configured =
    (process.env.REACT_APP_FRONTEND_PUBLIC_URL ||
      process.env.REACT_APP_SEARCHJOBAI_PUBLIC_URL ||
      '').trim().replace(/\/+$/, '');
  if (configured) {
    return configured;
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:3000`;
    }
  }

  return '';
}

function LiveInterviewFrontendHandoff() {
  const location = useLocation();

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const targetOrigin = resolveLiveInterviewFrontendOrigin();
    if (!targetOrigin) return;

    const nextUrl = `${targetOrigin}${location.pathname}${location.search}${location.hash}`;
    const currentUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    if (nextUrl !== currentUrl) {
      window.location.replace(nextUrl);
    }
  }, [location.hash, location.pathname, location.search]);

  const targetOrigin = resolveLiveInterviewFrontendOrigin();

  return (
    <div style={{ padding: '32px', maxWidth: 720, margin: '0 auto', lineHeight: 1.5 }}>
      <h1 style={{ fontSize: '1.8rem', marginBottom: '0.75rem' }}>Opening live interview room...</h1>
      <p style={{ marginBottom: '0.75rem' }}>
        This app does not host the live interview meeting UI. Redirecting you to the dedicated
        meeting frontend now.
      </p>
      {targetOrigin ? (
        <p style={{ wordBreak: 'break-word' }}>
          If the redirect does not happen automatically, open:{' '}
          <a href={`${targetOrigin}${location.pathname}${location.search}${location.hash}`}>
            {`${targetOrigin}${location.pathname}${location.search}${location.hash}`}
          </a>
        </p>
      ) : (
        <p>
          Set <code>REACT_APP_FRONTEND_PUBLIC_URL</code> or start the SearchJobAI frontend on port
          <code> 3000</code>.
        </p>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <DesktopAuthGate />
        <Routes>
          <Route path="/interview-room/*" element={<LiveInterviewFrontendHandoff />} />
          <Route path="/interview-booking/*" element={<LiveInterviewFrontendHandoff />} />
          {/* Organizer Routes */}
          <Route path="/organizer" element={
            <PublicRoute>
              <OrganizerLanding />
            </PublicRoute>
          } />
          <Route path="/organizer/login" element={
            <PublicRoute>
              <OrganizerLogin />
            </PublicRoute>
          } />
          <Route path="/organizer/register" element={
            <PublicRoute>
              <OrganizerRegister />
            </PublicRoute>
          } />
          <Route path="/organizer/onboarding" element={
            <PublicRoute>
              <OnboardingRealtime />
            </PublicRoute>
          } />
          <Route path="/organizer/dashboard" element={
            <PublicRoute>
              <OrganizerDashboard />
            </PublicRoute>
          } />
          
          {/* Participant Routes */}
          <Route path="/participant/verify-email" element={
            <PublicRoute>
              <EmailVerification />
            </PublicRoute>
          } />
          <Route path="/participant/invite/:inviteToken" element={
            <PublicRoute>
              <InviteLanding />
            </PublicRoute>
          } />
          <Route path="/participant/complete/:sessionId" element={
            <PublicRoute>
              <CompletePage />
            </PublicRoute>
          } />
          <Route path="/interview-feedback" element={
            <PublicRoute>
              <InterviewerFeedbackPage />
            </PublicRoute>
          } />
        <Route path="/create-resume" element={<ResumeLandingPage />} />
        {/* Public token-authenticated page for the phone side of the video
            resume QR handoff — no login on mobile. */}
        <Route path="/video-resume/mobile/:token" element={<MobileVideoUpload />} />
        <Route path="/cv-templates" element={<CvTemplatesPage />} />

          {/* ✅ Public Routes (Accessible only when NOT logged in) */}
          <Route path="/signin" element={
            <PublicRoute>
              <SignupForm />
            </PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          <Route path="/employer-forgot-password" element={
            <PublicRoute>
              <EmpForgotPass />
            </PublicRoute>
          } />

          <Route path="/reset-password-employer/:token" element={
            <PublicRoute>
              <EmpResetPass />
            </PublicRoute>
          } />

          <Route path="/reports" element={
            <PublicRoute>
              <Reports/>
            </PublicRoute>
          } />

          <Route path="/signin_user" element={
            <PublicRoute>
              <UserSignupForm />
            </PublicRoute>
          } />
          <Route path="/login_user" element={

            <PublicRoute><UserLoginPage /></PublicRoute>

          } />

          {/*
            LinkedIn OAuth callback. Not wrapped in PublicRoute because the
            redirect from LinkedIn can land here regardless of session state
            (e.g. a stale tab where the user was already partly logged in).
            LinkedInCallback itself reads sessionStorage to know whether the
            flow was 'user' or 'company' and routes accordingly.
          */}
          <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />

          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />

          <Route path="/reset-password/:token" element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } />

          {/* <Route path="/company-admin-home/*" element={
            <PublicRoute>
              <CompanyAdmin />
            </PublicRoute>
          } /> */}
          {/* <Route path="/company-admin-home" element={
            <PublicRoute> 
              <CompanyAdmin />
            </PublicRoute>
          }>
            <Route index element={<Content />} />

            <Route path="dashboard" element={<Content />} />
            <Route path="companies" element={<Content />} />
            <Route path="add-profile" element={<Content />} />

            <Route path="companies/:companyId/profile" element={<Content />} />
            <Route path="companies/:companyId/edit" element={<Content />} />

            <Route path="companies/:companyId/jobs" element={<Content />} />
            <Route path="companies/:companyId/createJD" element={<Content />} />
            <Route path="companies/:companyId/uploadJD" element={<Content />} />

            <Route path="companies/:companyId/jobs/:jobId/description" element={<Content />} />
            <Route path="companies/:companyId/jobs/:jobId/scheduled-tests" element={<Content />} />
            <Route path="companies/:companyId/jobs/:jobId/candidates" element={<Content />} />
            <Route path="companies/:companyId/jobs/:jobId/edit" element={<Content />} />

            <Route path="*" element={<Content />} />
          </Route> */}
          {/* dashboard */}

                    <Route path="/login-admin" element={
                      <PublicRoute>
                        <AdminLogin />
                      </PublicRoute>
                    } />
                    <Route path="/candidate-admin-home" element={
                      <AdminPrivateRoute>
                        <UserAdmin />
                      </AdminPrivateRoute>
                    } />
          
                    <Route path="/all-candidate" element={
                      <AdminPrivateRoute>
                        <UserAdmin />
                      </AdminPrivateRoute>
                    } />
          
                    <Route path="/add-candidate" element={
                      <AdminPrivateRoute>
                        <UserAdmin />
                      </AdminPrivateRoute>
                    } />
          
                    <Route path="/candidate-profile" element={
                      <AdminPrivateRoute>
                        <UserAdmin />
                      </AdminPrivateRoute>
                    } />
          
                    <Route path="/reports" element={
                      <PublicRoute>
                        <Reports/>
                      </PublicRoute>
                    } />
          <Route path="/company-admin-home" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          {/* all-companies */}
          <Route path="/all-companies" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/add-company-profile" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/company-profile" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/all-jobs" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/create-JD" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/upload-JD" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/after-JD" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/edit-company-profile" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/job-description" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/all-candidates" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/edit-job" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />
          <Route path="/view-test" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          <Route path="/super-admin-management" element={
            <AdminPrivateRoute>
              <CompanyAdmin />
            </AdminPrivateRoute>
          } />

          {/* <Route path="/" element={
            <PublicRoute>
              <CompanyAdmin />
            </PublicRoute>
          } />

          <Route path="/" element={
            <PublicRoute>
              <CompanyAdmin />
            </PublicRoute>
          } />

          <Route path="/" element={
            <PublicRoute>
              <CompanyAdmin />
            </PublicRoute>
          } />

          <Route path="/" element={
            <PublicRoute>
              <CompanyAdmin />
            </PublicRoute>
          } /> */}
        


          {/* ✅ Private Routes (Require Authentication) */}
          <Route path="/questionnaire" element={

            <UserPrivateRoute><QuestionnairePage /></UserPrivateRoute>

          } />

          <Route path="/user-home" element={

            <UserPrivateRoute><Home /></UserPrivateRoute>

          } />
          {/* For Mock Tests */}
          <Route path="/mock" element={

            <UserPrivateRoute><Mock /></UserPrivateRoute>

          } />
          <Route path="/mock-result" element={

            <UserPrivateRoute><TestResults /></UserPrivateRoute>

          } />
          <Route path="/instructions" element={

            <TestEntryGate />

          } />
          <Route path="/api/interview" element={

            <UserPrivateRoute><Interview /></UserPrivateRoute>

          } />
          <Route path="/questions" element={

            <UserPrivateRoute><Questions /></UserPrivateRoute>

          } />
          <Route path="/real-time-questions" element={

            <UserPrivateRoute><RealtimeQuestionSection /></UserPrivateRoute>

          } />
          <Route path="/results" element={

            <CompanyPrivateRoute><Resultv2 /></CompanyPrivateRoute>

          } />

          {/* ✅ Unprotected Routes (Accessible by all users) */}
          <Route path="/" element={

            <RecruitmentHero />

          }
          />
          <Route path="/aboutus" element={

            <About />
          } />
          <Route path="/contactus" element={

            <ContactPage />
          } />
          <Route path="/companyhome" element={

            <CompanyPrivateRoute><CompanyHome /></CompanyPrivateRoute>
          } />
          <Route path="/ch" element={

            <CompanyPrivateRoute><AllJob /></CompanyPrivateRoute>
          } />
          <Route path="/ranking" element={

            <CompanyPrivateRoute><Ranking /></CompanyPrivateRoute>
          } />
          <Route path="/jd" element={
            <CompanyPrivateRoute><Jd /></CompanyPrivateRoute>
          } />
          <Route path="/afterjd" element={
            <CompanyPrivateRoute><JobDescriptionPreview /></CompanyPrivateRoute>
          } />
          <Route path="/afterlogin" element={<AfterLogin />} />
          <Route path="/screening" element={<Screening />} />
          <Route path="/screening_test" element={<AfterGetStartedOnScreeningTest />} />
          <Route path="/personality_test" element={<PersonalityTest />} />
          <Route path="/overall_test" element={<OverallTest />} />
          <Route path="/thankyou" element={<UserPrivateRoute><Thankyou /></UserPrivateRoute>} />
          <Route path="/user" element={
            <UserDashboard />
          } />
          <Route path="/test" element={
            <CompanyPrivateRoute><ScheduleTest /></CompanyPrivateRoute>
          } />
          <Route path="/uploadjd" element={
            <CompanyPrivateRoute><UploadJobDescription /></CompanyPrivateRoute>
          } />
          
          <Route path="/ind" element={
            <CompanyPrivateRoute><MainResult /></CompanyPrivateRoute>
          } />
          <Route path="/default" element={
            <UserPrivateRoute><InputDesign /></UserPrivateRoute>
          } />

          <Route path="/practice" element={
            <UserPrivateRoute><PPractice /></UserPrivateRoute>
          } />
          <Route path="/crr" element={
            <UserPrivateRoute><CreateResume /></UserPrivateRoute>
          } />
          <Route path="/user/account" element={
            <UserPrivateRoute><AccountSecurity /></UserPrivateRoute>
          } />
          <Route path="/video" element={
            <PublicRoute><StartVideo /></PublicRoute>
          } />
          <Route path="/icoding" element={
            <UserPrivateRoute><Coding /></UserPrivateRoute>
          } />
          <Route path="/qcoding" element={
            <UserPrivateRoute><QuickCodingTest /></UserPrivateRoute>
          } />
          <Route path="/aptitude" element={
            <UserPrivateRoute><AptitudeTest /></UserPrivateRoute>
          } />
          <Route path="/jdai" element={
            <PublicRoute>
              <JdAi />
            </PublicRoute>
          } />
          <Route path="/jdaudio" element={
            <PublicRoute>
              <JdAudioAI />
            </PublicRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
